import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import axios from 'axios';
import { apiBaseUrl, updatePost } from '../../../util/api';
import { required } from '../../../util/validators';
import { Modal, PrimaryButton, FieldTextInput, Form } from '../../../components';
import css from './CreatePostForm.module.css';

const CreatePostForm = (props) => {
    const {
        postOpen,
        setPostOpen,
        onAddPost,
        postToEdit,
        posts,
        setPosts,
        onManageDisableScrolling,
    } = props;

    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file && file.type.startsWith('image/')) {
            setImage(file);
        } else {
            alert("Please select an image file.");
        };
    };

    const onSubmit = async (values) => {
        if (postToEdit) {
            return onUpdatePost(postToEdit._id, values);
        };

        if (!image) {
            alert("Please provide an image.");
            return;
        };

        const {
            year,
            month,
            day,
            title,
            content,
            link,
        } = values;

        setLoading(true);

        const formData = new FormData();
        formData.append("file", image);
        formData.append("year", year);
        formData.append("month", month);
        formData.append("day", day);
        formData.append("title", title);
        formData.append("content", content);
        formData.append("link", link);

        try {
            const response = await axios.post(
                `${apiBaseUrl()}/api/blog/create-post`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    // The server checks the admin's session cookie. Same-origin
                    // requests send it anyway; the dev API server is on another port.
                    withCredentials: true,
                }
            );

            onAddPost(response.data);
            setPostOpen(false);
        } catch (err) {
            console.error("Upload error:", err);
            alert("Failed to create post.");
        } finally {
            setLoading(false);
        };
    };

    const onUpdatePost = (_id, values) => {
        setLoading(true);

        const { year, month, day, ...rest } = values;
        const params = {
            ...rest,
            date: new Date(year, month - 1, day),
            _id: postToEdit._id,
        };

        updatePost(params)
            .then(() => {
                setPostOpen(false);
                const allOtherPosts = posts.filter(p => p._id !== _id);
                setPosts([
                    // params only holds the edited fields, so merge it over the
                    // existing post. Otherwise the fields the form never touches
                    // (pictureURL, pictureKey, closed) are dropped and the card
                    // loses its image until the page is reloaded.
                    { ...postToEdit, ...params },
                    ...allOtherPosts,
                ]);
            })
            .catch(() => {
                alert("Failed to update post.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const getInitialValues = () => {
        if (postToEdit) {
            return {
                content: postToEdit.content,
                link: postToEdit.link,
                title: postToEdit.title,
                year: new Date(postToEdit.date).getFullYear(),
                month: new Date(postToEdit.date).getMonth() + 1,
                day: new Date(postToEdit.date).getDate(),
            };
        } else {
            return {};
        };
    };

    return <div>
        <Modal
            id="BP.CreatePost"
            isOpen={!!postOpen}
            onClose={() => setPostOpen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
            usePortal
        >
            <FinalForm
                onSubmit={onSubmit}
                initialValues={getInitialValues()}
                render={fieldRenderProps => {
                    const {
                        handleSubmit,
                        invalid,
                    } = fieldRenderProps;

                    const submitDisabled = invalid || loading || (!image && !postToEdit);

                    return (
                        <Form className={css.form} onSubmit={handleSubmit}>
                            <FieldTextInput
                                className={css.input}
                                type="text"
                                id="year"
                                name="year"
                                label="Year"
                                placeholder="2025"
                                validate={required("Required")}
                            />
                            <FieldTextInput
                                className={css.input}
                                type="text"
                                id="month"
                                name="month"
                                label="Month"
                                placeholder="8"
                                validate={required("Required")}
                            />
                            <FieldTextInput
                                className={css.input}
                                type="text"
                                id="day"
                                name="day"
                                label="Day"
                                placeholder="15"
                                validate={required("Required")}
                            />

                            <FieldTextInput
                                className={css.input}
                                type="text"
                                id="title"
                                name="title"
                                label="Title"
                                placeholder="title"
                                validate={required("Required")}
                            />
                            <FieldTextInput
                                className={css.input}
                                type="textarea"
                                id="content"
                                name="content"
                                label="Content"
                                placeholder="content..."
                                validate={required("Required")}
                            />
                            <FieldTextInput
                                className={css.input}
                                type="text"
                                id="link"
                                name="link"
                                label="Unique link (only letters, numbers and dashes)"
                                placeholder="new-year-party"
                                validate={required("Required")}
                            />

                            {postToEdit ? null : <div>
                                <label>Picture</label>
                                <input
                                    className={css.input}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                            </div>}

                            <PrimaryButton
                                className={css.submitButton}
                                type="submit"
                                inProgress={loading}
                                disabled={submitDisabled}
                            >
                                Submit
                            </PrimaryButton>
                        </Form>
                    );
                }}
            />
        </Modal>
    </div>
};

export default CreatePostForm;
